import axios, { AxiosHeaders, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { CURRENT_STORE_STORAGE_KEY } from './authService'

export const AUTH_TOKEN_STORAGE_KEY = 'shopora_store_admin_token'
export const AUTH_REFRESH_TOKEN_STORAGE_KEY = 'shopora_store_admin_refresh_token'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

/** Resolved API origin + `/api/v1` prefix (for `fetch` uploads — avoids Axios JSON transform on FormData). */
export function apiBaseUrl(): string {
  return String(api.defaults.baseURL || '/api/v1').replace(/\/$/, '')
}

export function getStoredAccessToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
}

export function persistTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, accessToken)
  localStorage.setItem(AUTH_REFRESH_TOKEN_STORAGE_KEY, refreshToken)
}

const STORAGE_USER = 'shopora_store_admin_user'

function readStoredSupportAccessMode(): 'read_only' | 'full' | null {
  try {
    const raw = localStorage.getItem(STORAGE_USER)
    if (!raw) return null
    const user = JSON.parse(raw) as { supportSession?: { accessMode?: string } }
    const mode = user?.supportSession?.accessMode
    return mode === 'full' || mode === 'read_only' ? mode : null
  } catch {
    return null
  }
}

export class ReadOnlySupportSessionError extends Error {
  constructor() {
    super('This support session is read-only. Writes are blocked.')
    this.name = 'ReadOnlySupportSessionError'
  }
}

type SessionRefreshHandlers = { onStart: () => void; onEnd: () => void }
let sessionRefreshHandlers: SessionRefreshHandlers | null = null

export function setSessionRefreshHandlers(h: SessionRefreshHandlers | null) {
  sessionRefreshHandlers = h
}

/**
 * Login / change-password failures — do not treat as session expiry and do not refresh.
 */
function isCredentialAuthError(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false
  const code = (data as { error?: { code?: string } | null }).error?.code
  return code === 'INVALID_CREDENTIALS' || code === 'INVALID_CURRENT_PASSWORD'
}

/**
 * True when the session should end (expired/invalid/missing auth), not for wrong login password
 * or wrong "current password" on change-password (`INVALID_CREDENTIALS`, `INVALID_CURRENT_PASSWORD`).
 */
export function isSessionExpiredApiError(status: number, data: unknown): boolean {
  if (status !== 401) return false
  if (isCredentialAuthError(data)) return false
  return true
}

/** Attempt refresh for any auth failure except credential errors and an already-invalid refresh token. */
function shouldAttemptRefresh(status: number, data: unknown): boolean {
  if (status !== 401) return false
  if (isCredentialAuthError(data)) return false
  if (!data || typeof data !== 'object') return true
  const code = (data as { error?: { code?: string } | null }).error?.code
  if (code === 'INVALID_REFRESH_TOKEN') return false
  return true
}

let sessionInvalidHandler: (() => void) | null = null

/** Registered by `AuthProvider` so API layer can clear auth when the access token is no longer valid. */
export function setSessionInvalidHandler(handler: (() => void) | null) {
  sessionInvalidHandler = handler
}

function notifySessionInvalid(err: { response?: { status?: number; data?: unknown } }) {
  const status = err.response?.status
  const data = err.response?.data
  if (status !== undefined) {
    notifySessionInvalidFromHttpResponse(status, data)
  }
}

/** For non-Axios callers (`fetch` uploads, etc.). */
export function notifySessionInvalidFromHttpResponse(status: number, data: unknown) {
  if (isSessionExpiredApiError(status, data)) {
    sessionInvalidHandler?.()
  }
}

let refreshInFlight: Promise<void> | null = null

interface RefreshEnvelope {
  success: boolean
  data?: { accessToken: string; refreshToken: string }
}

async function performTokenRefresh(): Promise<void> {
  const rt = getStoredRefreshToken()
  if (!rt) throw new Error('No refresh token')
  sessionRefreshHandlers?.onStart()
  try {
    const { data } = await api.post<RefreshEnvelope>('/auth/refresh', { refreshToken: rt }, { skipAuthRefresh: true })
    const payload = data?.data
    if (!data?.success || !payload?.accessToken || !payload?.refreshToken) {
      throw new Error('Invalid refresh response')
    }
    persistTokens(payload.accessToken, payload.refreshToken)
  } finally {
    sessionRefreshHandlers?.onEnd()
  }
}

function refreshTokensSingleFlight(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = performTokenRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

api.interceptors.request.use((config) => {
  const method = String(config.method ?? 'get').toLowerCase()
  if (method !== 'get' && method !== 'head' && method !== 'options') {
    if (readStoredSupportAccessMode() === 'read_only') {
      return Promise.reject(new ReadOnlySupportSessionError())
    }
  }

  const token = getStoredAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const storeId = localStorage.getItem(CURRENT_STORE_STORAGE_KEY)
  if (storeId) {
    config.headers['X-Store-Id'] = storeId
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const cfg = error.config as InternalAxiosRequestConfig | undefined
    if (!error.response || !cfg) {
      return Promise.reject(error)
    }

    if (cfg.skipAuthRefresh) {
      if (cfg.url?.includes('/auth/refresh') && error.response.status === 401) {
        sessionInvalidHandler?.()
      }
      return Promise.reject(error)
    }

    const status = error.response.status
    const data = error.response.data

    if (
      status === 401 &&
      shouldAttemptRefresh(status, data) &&
      getStoredRefreshToken() &&
      !cfg._retry
    ) {
      cfg._retry = true
      try {
        await refreshTokensSingleFlight()
        const token = getStoredAccessToken()
        if (!token) throw new Error('Missing access token after refresh')
        const next = AxiosHeaders.from(cfg.headers ?? {})
        next.set('Authorization', `Bearer ${token}`)
        cfg.headers = next
        return api.request(cfg)
      } catch {
        sessionInvalidHandler?.()
        return Promise.reject(error)
      }
    }

    notifySessionInvalid(error)
    return Promise.reject(error)
  },
)
